import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useState } from 'react';
import { reportError } from '@/shared/utils/reportError';
import { comandosDaSuperficie, FalhaDaSimulacao } from '../store/services';
import type { PreviaDaMidia, SuperficieDaSimulacao } from '../types';
import type { LeituraDaMidia } from '../types/hookTypes';
import { classificarLink } from '../utils/classificarLink';
const relatarFalha = (erro: unknown, ids: {
  simulacaoId: string;
  arquivoId: string;
}) => reportError(erro instanceof Error ? erro : new Error('Falha ao carregar a prévia da mídia'), {
  source: 'negociacaoSimulacao',
  acao: 'previaDaMidia',
  ...ids
});

/**
 * Hook de efeito de navegador (exceção escrita da regra #1), no padrão do documento da
 * assinatura: a URL de 60 s é credencial de leitura e vive só aqui, nunca no Redux (CA-18).
 *
 * Áudio e vídeo pedem a mídia em várias requisições de faixa, e uma delas pode cair depois que o
 * acesso venceu. Cada erro do elemento pede um acesso novo; se o acesso novo também falhar antes
 * de carregar, a prévia some, para um arquivo quebrado não virar laço de requisições.
 * 404 é o arquivo que não existe: quem chama o mostra como 'Arquivo indisponível'.
 */
export function usePreviaDaMidia(simulacaoId, arquivoId, aoFicarIndisponivel, t0) {
  const $ = _c(26);
  const superficie = t0 === undefined ? "ESCRITORIO" : t0;
  const [tentativa, setTentativa] = useState(0);
  const [renovando, setRenovando] = useState(false);
  const [leitura, setLeitura] = useState(null);
  const arquivo = simulacaoId && arquivoId ? `${simulacaoId}/${arquivoId}` : null;
  let t1;
  if ($[0] !== aoFicarIndisponivel) {
    t1 = id => aoFicarIndisponivel(id);
    $[0] = aoFicarIndisponivel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const avisarIndisponivel = useEffectEvent(t1);
  let t2;
  if ($[2] !== arquivo || $[3] !== arquivoId || $[4] !== avisarIndisponivel || $[5] !== simulacaoId || $[6] !== superficie || $[7] !== tentativa) {
    t2 = () => {
      if (!arquivo || !simulacaoId || !arquivoId) {
        return;
      }
      let ativo = true;
      const ids = {
        simulacaoId,
        arquivoId
      };
      const concluir = resultado => {
        if (ativo) {
          setLeitura(resultado);
        }
      };
      comandosDaSuperficie(superficie).acessarArquivo(simulacaoId, arquivoId).then(t3 => {
        const {
          url
        } = t3;
        const destino = classificarLink(url);
        if (destino?.safe) {
          concluir({
            arquivo,
            tentativa,
            status: "pronto",
            url: destino.href
          });
          return;
        }
        relatarFalha(new Error("URL da pr\xE9via de m\xEDdia fora de https"), ids);
        concluir({
          arquivo,
          tentativa,
          status: "falhou"
        });
      }, erro => {
        if (erro instanceof FalhaDaSimulacao && erro.erro.status === 404) {
          if (ativo) {
            avisarIndisponivel(ids.arquivoId);
          }
        } else {
          relatarFalha(erro, ids);
        }
        concluir({
          arquivo,
          tentativa,
          status: "falhou"
        });
      });
      return () => {
        ativo = false;
      };
    };
    $[2] = arquivo;
    $[3] = arquivoId;
    $[4] = avisarIndisponivel;
    $[5] = simulacaoId;
    $[6] = superficie;
    $[7] = tentativa;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== arquivo || $[10] !== arquivoId || $[11] !== simulacaoId || $[12] !== superficie || $[13] !== tentativa) {
    t3 = [arquivo, arquivoId, simulacaoId, superficie, tentativa];
    $[9] = arquivo;
    $[10] = arquivoId;
    $[11] = simulacaoId;
    $[12] = superficie;
    $[13] = tentativa;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  useEffect(t2, t3);
  const leituraDoArquivo = leitura?.arquivo === arquivo ? leitura : null;
  const leituraDaTentativa = leituraDoArquivo?.tentativa === tentativa ? leituraDoArquivo : null;
  let t4;
  if ($[15] !== renovando) {
    t4 = () => {
      if (renovando) {
        setRenovando(false);
      }
    };
    $[15] = renovando;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const registrarCarga = t4;
  let t5;
  if ($[17] !== arquivoId || $[18] !== leituraDaTentativa || $[19] !== renovando || $[20] !== simulacaoId) {
    t5 = () => {
      if (leituraDaTentativa?.status !== "pronto" || !simulacaoId || !arquivoId) {
        return;
      }
      if (renovando) {
        relatarFalha(new Error("Pr\xE9via de m\xEDdia n\xE3o carregou nem com acesso novo"), {
          simulacaoId,
          arquivoId
        });
        setLeitura({
          arquivo: leituraDaTentativa.arquivo,
          tentativa: leituraDaTentativa.tentativa,
          status: "falhou"
        });
        return;
      }
      setRenovando(true);
      setTentativa(_temp);
    };
    $[17] = arquivoId;
    $[18] = leituraDaTentativa;
    $[19] = renovando;
    $[20] = simulacaoId;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const registrarErro = t5;
  const url_0 = leituraDoArquivo?.status === "pronto" ? leituraDoArquivo.url : null;
  let t6;
  if ($[22] !== registrarCarga || $[23] !== registrarErro || $[24] !== url_0) {
    t6 = {
      url: url_0,
      registrarCarga,
      registrarErro
    };
    $[22] = registrarCarga;
    $[23] = registrarErro;
    $[24] = url_0;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
function _temp(anterior) {
  return anterior + 1;
}